<div
  class="call-patient-dialog"
  cdkDrag
  cdkDragRootElement=".cdk-overlay-pane"
  [cdkDragFreeDragPosition]="dragPosition"
  (cdkDragEnded)="onDragEnded($event)"
  [ngClass]="{ minimized: !contentVisible && callInProgress }"
  cdkDragBoundary=".cdk-global-overlay-wrapper"
>
  <form [formGroup]="callForm" (ngSubmit)="doCall()" class="call-form">
    <div class="header-container">
      <h2 *ngIf="!callInProgress">{{ data?.isExternal ? 'Make external call' : 'Call patient' }}</h2>
      <h2 *ngIf="callInProgress">{{ additionalParticipantCallSid ? 'Conference Call' : 'Call in Progress' }}</h2>
      <div
        *ngIf="!contentVisible && callInProgress && callForm.get('via')?.value === 'browser'"
        class="timer-display"
        [ngStyle]="{ 'font-size': '18px' }"
      >
        {{ durationFormatted }}
      </div>
      <app-call-signal-indicator
        *ngIf="!contentVisible && callInProgress && callForm.get('via')?.value === 'browser'"
        [quality]="callQuality"
        [compact]="true"
      ></app-call-signal-indicator>
      <button
        type="button"
        *ngIf="callInProgress && callForm.get('via')?.value === 'browser'"
        class="button-icon"
        mat-button
        (click)="toggleSize()"
        [matTooltip]="contentVisible ? 'Minimize' : 'Maximize'"
      >
        <mat-icon>{{ !contentVisible ? 'fullscreen' : 'minimize' }}</mat-icon>
      </button>
    </div>
    <div
      class="call-form-fields-container"
      [ngStyle]="{ display: callInProgress && callForm.get('via')?.value === 'browser' ? 'none' : 'flex' }"
    >
      <mat-form-field
        appearance="outline"
        *ngIf="
          provider?.twilio_line?.length > 1 &&
          !(data?.isExternal && isSingleTwilioLinePerClient) &&
          !clinicPhoneOptions.length
        "
      >
        <span matPrefix style="padding-right: 16px"><mat-icon class="contact-icon">phone</mat-icon></span>
        <mat-select #select formControlName="twilioNumber" panelClass="call-patient-dialog__select-panel">
          <mat-option *ngFor="let twilioNumber of provider?.twilio_line" [value]="twilioNumber">
            {{ twilioNumber }}
          </mat-option>
        </mat-select>
        <mat-hint>Select a phone number to dial from</mat-hint>
      </mat-form-field>
      <!--
        Nothing renders in place of this dropdown for a canary user. The story is written "As a system"
        and asks for the selection to be reflected in the call log, not in this dialog — and in the
        automatic flow the staff has no override, so naming a number they cannot change would raise
        questions they cannot act on.
      -->
      <mat-form-field appearance="outline" *ngIf="clinicPhoneOptions.length && !usesAutomaticCallerId">
        <mat-label>Clinic phone number</mat-label>
        <span matPrefix style="padding-right: 16px"><mat-icon class="contact-icon">phone</mat-icon></span>
        <mat-select formControlName="twilioNumber" panelClass="call-patient-dialog__select-panel">
          <mat-option
            *ngFor="let clinicPhone of clinicPhoneOptions"
            [value]="clinicPhone.phoneNumber"
          >
            {{ utilService.formatPhoneNumber(clinicPhone.phoneNumber) }}
            <ng-container *ngIf="clinicPhone.region"> - {{ clinicPhone.region }}</ng-container>
          </mat-option>
        </mat-select>
        <mat-hint>Select the clinic phone number to call from</mat-hint>
      </mat-form-field>
      <mat-form-field appearance="outline" *ngIf="data?.isExternal && isSingleTwilioLinePerClient">
        <span matPrefix style="padding-right: 16px"><mat-icon class="contact-icon">phone</mat-icon></span>
        <mat-select #select formControlName="twilioNumberPerClient" panelClass="call-patient-dialog__select-panel">
          <mat-option *ngFor="let phoneConfig of clientPhoneConfigList" [value]="phoneConfig.phoneNumber">
            {{ utilService.formatPhoneNumber(phoneConfig.phoneNumber) }} - {{ phoneConfig.clientName }}
          </mat-option>
        </mat-select>
        <mat-hint>Select a phone number to dial from</mat-hint>
      </mat-form-field>
      <!--
        Nothing renders in its place outside the pilot. The call then behaves exactly as it did before
        this feature: the staff member stays on the line and speaks the message, which is also the
        default inside the pilot — so the control only ever adds options, never removes one.

        Hidden on external calls too. There the number is free text, so one wrong digit would play a
        program-specific message into a stranger's mailbox with no human in the loop — on the live path
        the staff member hears the greeting and can hang up, and that check is exactly what a
        pre-recorded message removes.
      -->
      <mat-form-field appearance="outline" *ngIf="canChooseVoicemailMessage && !data?.isExternal">
        <span matPrefix style="padding-right: 16px"><mat-icon class="contact-icon">voicemail</mat-icon></span>
        <mat-select formControlName="voicemailMessageId" panelClass="call-patient-dialog__select-panel">
          <mat-option [value]="voicemailSelection.LIVE">Leave a live message</mat-option>
          <mat-option *ngFor="let message of voicemailMessages$ | async" [value]="message.id">
            {{ message.label }}
          </mat-option>
          <mat-option [value]="voicemailSelection.NONE">Do not leave a message</mat-option>
        </mat-select>
        <mat-hint>If the call reaches voicemail</mat-hint>
      </mat-form-field>
      <mat-form-field appearance="outline" *ngIf="data.isExternal; else contactFixed">
        <mat-label>Phone Number</mat-label>
        <span class="call-external-prefix" matPrefix>+1</span>
        <input matInput formControlName="contactNumber" appPhoneMask />
      </mat-form-field>
      <ng-template #contactFixed>
        <mat-form-field>
          <span [style.display]="select?.value ? 'block' : 'none'" matPrefix style="padding-right: 16px"
            ><mat-icon class="contact-icon">{{ getIconFromType(select.value?.contact_type) }}</mat-icon></span
          >
          <mat-select #select formControlName="contactNumber" panelClass="call-patient-dialog__select-panel">
            <mat-select-trigger
              [matTooltip]="selectedContactTooltip"
              [matTooltipDisabled]="!selectedContactTooltip"
              matTooltipClass="call-patient-dialog__contact-tooltip"
            >
              {{ selectedContact?.contact_data }}
            </mat-select-trigger>
            <mat-option
              *ngFor="let contact of patientContacts$ | async"
              [value]="contact"
              [matTooltip]="getContactTooltip(contact)"
              [matTooltipDisabled]="!getContactTooltip(contact)"
              matTooltipClass="call-patient-dialog__contact-tooltip"
            >
              {{ contact.contact_data }}
            </mat-option>
          </mat-select>
          <mat-hint>Select a phone number to dial</mat-hint>
        </mat-form-field>
      </ng-template>
    </div>
    <!--
      Sits below the selectors in the pre-call phase, but as a SIBLING of .call-form-fields-container,
      never a child: that container is display:none during the call and this panel has to survive into
      the in-call phase. See showCallerIdPanel in the component.

      It states the line NP-04 resolved — including for a canary user, where the dropdown above is
      gone. NP-04 rendered nothing there because its story is written "As a system"; this feature's
      story asks for the number to be visible in both phases, so the panel adds the display without
      touching the selection.

      Canary-gated on TWO conditions (the user's email in outboundCallerIdCanary, and the patient's
      client on isSingleTwilioLinePerClient) — see callerIdVisibilityEnabled. Outside the canary this
      whole block is absent and the dialog renders as it did before this story.
    -->
    <div
      class="caller-id"
      [class.caller-id--compact]="callInProgress"
      [class.caller-id--error]="showCallerIdMissing && !showCallerIdChoicePending"
      [class.caller-id--pending]="showCallerIdChoicePending"
      *ngIf="showCallerIdPanel"
      role="status"
      aria-live="polite"
    >
      <ng-container *ngIf="showCallerIdResolving; else callerIdResolved">
        <mat-spinner class="caller-id__spinner" [diameter]="16"></mat-spinner>
        <span class="caller-id__label">Resolving caller ID…</span>
      </ng-container>

      <ng-template #callerIdResolved>
        <ng-container *ngIf="hasDisplayedCallerId; else callerIdMissing">
          <mat-icon class="caller-id__icon">ring_volume</mat-icon>
          <div class="caller-id__body">
            <span class="caller-id__label">{{ callInProgress ? 'Patient sees' : 'Patient will see' }}</span>
            <span class="caller-id__number" [matTooltip]="displayedCallerId">{{ callerIdDisplay }}</span>
            <span class="caller-id__source" *ngIf="displayedCallerIdSourceLabel">
              {{ displayedCallerIdSourceLabel }}
            </span>
          </div>
        </ng-container>

        <ng-template #callerIdMissing>
          <mat-icon
            class="caller-id__icon"
            [class.caller-id__icon--error]="!showCallerIdChoicePending"
            [class.caller-id__icon--pending]="showCallerIdChoicePending"
          >
            {{ showCallerIdChoicePending ? 'touch_app' : 'phone_disabled' }}
          </mat-icon>
          <span class="caller-id__label caller-id__label--error">{{ callerIdMissingMessage }}</span>
        </ng-template>
      </ng-template>
    </div>
    <div class="microphone-alert" *ngIf="microphoneWarning && callInProgress" role="alert" aria-live="assertive">
      <mat-icon class="microphone-alert__icon">mic_off</mat-icon>
      <span class="microphone-alert__message">{{ microphoneWarning }}</span>
    </div>
    <div class="call-form-status" *ngIf="contentVisible && callInProgress && callForm.get('via')?.value === 'browser'">
      <mat-icon [ngClass]="'call-icon'">
        {{ additionalParticipantCallSid ? 'groups' : 'phone_in_talk' }}
      </mat-icon>
      <div class="timer-display-container">
        <mat-icon class="timer-icon" color="primary">schedule</mat-icon>
        <span class="timer-display">
          {{ durationFormatted }}
        </span>
      </div>
      <app-call-signal-indicator [quality]="callQuality"></app-call-signal-indicator>

      <!-- Participant Badge -->
      <div class="participant-badge" *ngIf="additionalParticipantCallSid">
        <div class="participant-info">
          <mat-icon>person_add</mat-icon>
          <span>{{ additionalParticipantNumber }}</span>
        </div>
        <button
          mat-mini-fab
          color="warn"
          class="hangup-button"
          (click)="hangUpParticipant()"
          matTooltip="Hang up"
          type="button"
        >
          <mat-icon>call_end</mat-icon>
        </button>
      </div>

      <mat-checkbox
        [class.remind-checkbox]="shouldRemind"
        (change)="toggleIdentityVerified()"
        formControlName="isIdentityVerified"
      >
        <span>Patient identity verified</span>
      </mat-checkbox>
      <mat-checkbox
        *ngIf="existingPatient && !data.isExternal"
        [class.remind-checkbox]="shouldRemindRecordConsent"
        (change)="toggleRecordConsent()"
        formControlName="isRecordConsent"
        [matTooltip]="
          !callForm.get('isRecordConsent')?.value
            ? 'Please ask the patient to consent to record the call'
            : 'Recording consent given'
        "
      >
        <span>Record consent</span>
      </mat-checkbox>
      <div class="call-dial">
        <app-dial (digit)="sendDigits($event)"></app-dial>
      </div>
    </div>

    <!-- Add Participant Form -->
    <div class="add-participant-container" [class.show]="showAddParticipant && callInProgress">
      <mat-form-field appearance="outline" class="participant-input">
        <mat-label>Add participant</mat-label>
        <input
          matInput
          type="tel"
          [errorStateMatcher]="errorStateMatcher"
          [formControl]="additionalParticipantPhoneNumberControl"
          placeholder="(555) 123-4567"
          inputmode="tel"
          (blur)="formatPhoneNumber(additionalParticipantPhoneNumberControl)"
          (keydown.enter)="handleEnterKey($event)"
          aria-label="Phone number to add as participant"
        />
        <mat-hint>Enter a 10-digit phone number or 1-800 number</mat-hint>
        <mat-error
          *ngIf="
            additionalParticipantPhoneNumberControl.hasError('required') &&
            additionalParticipantPhoneNumberControl.touched
          "
          >Phone number is required</mat-error
        >
        <mat-error
          *ngIf="
            additionalParticipantPhoneNumberControl.hasError('invalidPhone') &&
            additionalParticipantPhoneNumberControl.touched
          "
        >
          Please enter a valid 10-digit phone number or 1-800 number
        </mat-error>
      </mat-form-field>
      <button
        mat-raised-button
        color="primary"
        class="add-button"
        [disabled]="!additionalParticipantPhoneNumberControl.valid"
        (click)="addParticipant()"
      >
        <mat-icon>add</mat-icon>
        <span>Add</span>
      </button>
    </div>

    <div class="call-form-footer">
      <div class="microphone-blocked" *ngIf="microphoneBlocksCall && !callInProgress" role="alert">
        <mat-icon class="microphone-blocked__icon">mic_off</mat-icon>
        <span class="microphone-blocked__message">{{ microphoneBlockedMessage }}. Browser calls are disabled until it is fixed.</span>
        <button mat-button type="button" class="microphone-blocked__action" (click)="showMicrophoneHelp()">How to fix</button>
      </div>
      <div class="call-form-buttons-initial">
        <div class="action-buttons">
          <button mat-raised-button color="default" type="button" (click)="close()" *ngIf="!callInProgress">
            Cancel
          </button>
          <button
            mat-raised-button
            color="primary"
            *ngIf="!callInProgress"
            [disabled]="callForm.get('contactNumber')?.invalid || callingInProgress || callerIdDisablesCallButton || microphoneBlocksCall"
            type="submit"
            style="min-width: 100px"
          >
            <mat-spinner *ngIf="callingInProgress" [diameter]="20" style="display: inline-block"></mat-spinner>
            <ng-container *ngIf="!callingInProgress"><mat-icon>phone</mat-icon> Call</ng-container>
          </button>
        </div>
      </div>
      <div class="call-form-buttons">
        <ng-container *ngIf="provider?.enable_conference_call">
          <button
            mat-mini-fab
            *ngIf="callInProgress && callForm.get('via')?.value === 'browser' && !additionalParticipantCallSid"
            color="primary"
            matTooltip="Add participant"
            class="add-participant-button"
            (click)="toggleAddParticipant()"
            type="button"
          >
            <mat-icon>person_add</mat-icon>
          </button>
        </ng-container>
        <ng-container *ngIf="provider?.enable_conference_call">
          <button
            mat-mini-fab
            *ngIf="callInProgress && callForm.get('via')?.value === 'browser'"
            [color]="isOnHold ? 'accent' : 'primary'"
            [attr.aria-disabled]="holdPending"
            [attr.aria-busy]="holdPending"
            (click)="toggleHold()"
            type="button"
            class="call-form-buttons__hold"
            [attr.aria-label]="isOnHold ? 'Resume call' : 'Put call on hold'"
            [attr.aria-pressed]="isOnHold"
            [matTooltip]="isOnHold ? 'Resume' : 'Hold'"
          >
            <mat-spinner *ngIf="holdPending" [diameter]="20"></mat-spinner>
            <mat-icon *ngIf="!holdPending">{{ isOnHold ? 'play_arrow' : 'pause' }}</mat-icon>
          </button>
        </ng-container>
        <button
          mat-mini-fab
          [color]="isMuted ? 'warn' : 'primary'"
          *ngIf="callInProgress && callForm.get('via')?.value === 'browser'"
          (click)="toggleMute()"
          type="button"
          aria-label="Toggle mute"
          [matTooltip]="isMuted ? 'Unmute' : 'Mute'"
        >
          <mat-icon>{{ isMuted ? 'mic_off' : 'mic' }}</mat-icon>
        </button>
        <button
          mat-mini-fab
          color="warn"
          *ngIf="callInProgress && callForm.get('via')?.value === 'browser'"
          (click)="endCall()"
          aria-label="End call"
          matTooltip="End call"
        >
          <mat-icon>call_end</mat-icon>
        </button>
      </div>
    </div>
    <p class="info-section" *ngIf="callInProgress">
      Please allow access to your microphone and speakers in your web browser.
    </p>
    <mat-progress-bar mode="indeterminate" *ngIf="callInProgress && callForm.get('via')?.value !== 'browser'">
    </mat-progress-bar>
  </form>
</div>
